// helper
import { cloneDeep } from "lodash";
import set from "lodash/set";
import get from 'lodash/get';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/app';
import { getFirstLoginAccess, readTokenClaims, tokenTenantContext } from './firstLoginAccess';
import { buildAuthPayload } from './profile';
import { fetchUserProfile, requiresReauthentication } from './profileRequest';

const TOKEN_KEY = 'LOOPIAPP_Token';

function setCookie(name, value, hours = 2) {
  const expires = new Date(Date.now() + hours * 36e5).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/; Secure; SameSite=Strict`;
}

function getCookie(name) {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1] || null;
}

function deleteCookie(name) {
  document.cookie = `${name}=; Max-Age=0; path=/;`;
}

const handler = {
  set: function (obj, prop, value) {
    authService.observers.forEach((ob) => ob({ ...obj, [prop]: value }));
    set(obj, prop, value);
    return true;
  },
};

const normalizeArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

const normalizeBusinesses = (value) => {
  const list = normalizeArray(value);
  return list;
};

const areTenantListsEqual = (current = [], next = []) => {
  if (current === next) return true;
  if (current.length !== next.length) return false;

  return current.every((tenant, index) => {
    const nextTenant = next[index];
    return (
      tenant?.id === nextTenant?.id &&
      tenant?.commercialName === nextTenant?.commercialName &&
      tenant?.logoUrl === nextTenant?.logoUrl
    );
  });
};

const authService = {
    state: new Proxy(
        {
            getting: false,
            profileError: null,
            profileRefresh: 0,
            claimRecovery: null,
            user: {
                name: null,
                token: null,
                username: null,
                roles: null,
                permissions: null,
                isAuthenticated: false,
                profileUrl: null,
                status: null,
                userId: null,
                tenantId: null,
                memberships: [],
                profileClaims: null,
                profileLoaded: false,
            },
            businesses: {},
            client: {
              tenants: [],
              tenantSelected: 0,
              isLoadingTenants: false
            },
            catalogs:{
              categories:[]
            }
        },
        handler
    ),
    observers: [],
    sessionVersion: 0,
    subscribe: function (observer) {
        this.observers.push(observer)
        return () => { this.observers = this.observers.filter((item) => item !== observer) }
    },
    setGetting(val) { this.state.getting = val },
    setProfile(token, profile, firebaseUser, claims) {
        const payload = buildAuthPayload(token, profile, firebaseUser, claims, this.state.claimRecovery?.expectedTenantId);
        this.setLogin(payload.token, payload.displayName, payload.username, payload.roles,
            payload.permissions, payload.businesses, payload.profileUrl, payload.status,
            payload.userId, payload.tenantId, payload.memberships, payload.profileClaims);
        return payload;
    },
    async refreshMerchantSession() {
        if (this.state.claimRecovery?.attempted) return;
        const access = getFirstLoginAccess(this.state.user);
        if (!access.pending || !access.blockedReason || access.blockedReason === 'inactive') return;
        const firebaseUser = auth.currentUser;
        const version = ++this.sessionVersion;
        const recovery = { attempted: true, expectedTenantId: access.tenantId, status: 'refreshing' };
        this.state.claimRecovery = recovery;
        if (!firebaseUser || firebaseUser.uid !== readTokenClaims(this.state.user.token).sub) {
            this.state.claimRecovery = { ...recovery, status: 'reauthenticate' };
            return;
        }
        const isCurrent = () => this.sessionVersion === version && auth.currentUser === firebaseUser;
        try {
            // SDK forceRefresh=true issues a new ID token and returns its decoded claims.
            const { token, claims } = await firebaseUser.getIdTokenResult(true);
            if (!isCurrent()) return;
            const profile = await fetchUserProfile(token);
            if (!isCurrent()) return;
            this.setProfile(token, profile, firebaseUser, claims);
            this.state.claimRecovery = { ...recovery, status: 'done' };
        } catch (error) {
            if (!isCurrent()) return;
            this.state.claimRecovery = { ...recovery, status: requiresReauthentication(error) ? 'reauthenticate' : 'failed' };
        }
    },
    retryProfile() {
        this.state.profileError = null;
        this.state.getting = true;
        this.state.profileRefresh += 1;
    },
    completeFirstLogin(tenantId) {
        const user = cloneDeep(this.state.user);
        user.memberships = user.memberships.map((m) => m.tenantId === tenantId ? { ...m, firstLoginCompleted: true } : m);
        this.state.user = user;
    },
    setToken(val) {
        const user = cloneDeep(this.state.user);
        const previousClaims = readTokenClaims(user.token || '');
        const nextClaims = readTokenClaims(val);
        if (previousClaims.sub !== nextClaims.sub) {
            this.sessionVersion += 1;
            this.state.claimRecovery = null;
        }
        const sameUser = user.profileLoaded && previousClaims.sub === nextClaims.sub &&
            JSON.stringify(tokenTenantContext(user.token)) === JSON.stringify(tokenTenantContext(val));
        setCookie(TOKEN_KEY, val);
        user.token = val;
        user.isAuthenticated = true;
        if (!sameUser) {
            user.profileUrl = null;
            user.status = null;
            user.userId = null;
            user.tenantId = null;
            user.memberships = [];
            user.profileClaims = null;
            user.profileToken = null;
            user.profileLoaded = false;
            this.state.client = { tenants: [], tenantSelected: 0, isLoadingTenants: false };
            this.state.businesses = [];
        }
        this.state.getting = !sameUser;
        this.state.user = user;
    },
    setLogin(
      token,
      name,
      username,
      roles,
      permissions,
      businesses = [],
      profileUrl = null,
      status = null,
      userId = null,
      tenantId = null,
      memberships = [],
      profileClaims = null,
    ){
        const user = cloneDeep(this.state.user);
        const normalizedRoles = normalizeArray(roles);
        const normalizedPermissions = normalizeArray(permissions);
        const normalizedBusinesses = normalizeBusinesses(businesses);

        user.token = token;
        user.name = name;
        user.username = username;
        user.roles = normalizedRoles;
        user.permissions = normalizedPermissions;
        user.isAuthenticated = true;
        user.profileUrl = profileUrl || null;
        user.status = status || null;
        user.userId = userId || null;
        user.tenantId = tenantId || null;
        user.memberships = cloneDeep(memberships);
        user.profileClaims = cloneDeep(profileClaims);
        user.profileLoaded = true;
        user.profileToken = token;
        user.profileRefreshVersion = this.state.profileRefresh;

        setCookie(TOKEN_KEY, token);

        this.state.businesses = normalizedBusinesses.map((business) => cloneDeep(business));
        this.state.getting = false;
        this.state.profileError = null;
        this.state.user = user;
    },
    async logout(options = {}){
        this.sessionVersion += 1;
        const { skipFirebaseSignOut = false, reload = true } = options;
        const user = cloneDeep(this.state.user);

        if (!skipFirebaseSignOut) {
            try {
                await signOut(auth);
            } catch (error) {
                console.error('Error signing out from Firebase', error);
            }
        }

        deleteCookie(TOKEN_KEY);

        user.token = null;
        user.name = null;
        user.username = null;
        user.roles = null;
        user.isAuthenticated = false;
        user.profileUrl = null;
        user.status = null;
        user.userId = null;
        user.tenantId = null;
        user.permissions = [];
        user.memberships = [];
        user.profileClaims = null;
        user.profileLoaded = false;
        user.profileToken = null;

        this.state.user = user;
        this.state.businesses = [];
        this.state.client = { tenants: [], tenantSelected: 0, isLoadingTenants: false };
        this.state.profileError = null;
        this.state.claimRecovery = null;
        this.state.getting = false;

        if (reload) {
            window.location.reload();
        }
    },
    setTenants(tenants, tenantSelected) {
      const currentClient = this.state.client;

      if (
        currentClient.tenantSelected === tenantSelected &&
        areTenantListsEqual(currentClient.tenants, tenants)
      ) {
        return;
      }

      const client = cloneDeep(this.state.client)

      client.tenantSelected = tenantSelected;
      client.tenants = tenants;

      this.state.client = client;
    },
    setLoadingTenants(value) {
    if (this.state.client.isLoadingTenants === value) {
      return;
    }

    const client = cloneDeep(this.state.client)
    client.isLoadingTenants = value;

    this.state.client = client;
    },
    setTenantSelected(value) {
      if (this.state.client.tenantSelected === value) {
        return;
      }

      const client = cloneDeep(this.state.client)
      client.tenantSelected = value;

      this.state.client = client;
    },
    setCategories(categories){
      const catalogs = cloneDeep(this.state.catalogs)
      catalogs.categories = categories;

      this.state.catalogs = catalogs;
    },
    evalLogin(){
        const user = cloneDeep(this.state.user);
        const token = getCookie(TOKEN_KEY);

        if (token === null) return

        const tokenRaw = token.split('.')

        if (tokenRaw.length !== 3) return

        let rawUser = {}
        try {
            rawUser = JSON.parse(atob(tokenRaw[1]))
        } catch (e) {
            return
        }

        const username = get(rawUser, 'username', '') || get(rawUser, 'email', '') || get(rawUser, 'user_id', '') || get(rawUser, 'sub', '');
        const name = get(rawUser, 'name', '') || get(rawUser, 'displayName', '') || username;
        const userId = get(rawUser, 'user_id', null) || get(rawUser, 'sub', null) || null;
        const roles = normalizeArray(get(rawUser, 'roles', []));
        const permissions = normalizeArray(get(rawUser, 'permissions', []));
        const businessesClaim = get(rawUser, 'clients', undefined) !== undefined ? get(rawUser, 'clients', []) : get(rawUser, 'businesses', []);
        const normalizedBusinesses = normalizeBusinesses(businessesClaim);
        const profileUrl = get(rawUser, 'picture', null);

        this.state.businesses = normalizedBusinesses.map((business) => cloneDeep(business));

        user.token = token;
        user.name = name;
        user.username = username;
        user.roles = roles;
        user.permissions = permissions;
        user.isAuthenticated = true
        user.profileUrl = profileUrl || null;
        user.status = null;
        user.userId = userId;
        user.tenantId = null;
        user.memberships = [];
        user.profileClaims = null;
        user.profileToken = null;
        user.profileLoaded = false;

        this.state.getting = true;
        this.state.user = user;
    }
}

export default authService
